/**
 * NfcePdvDesktopService
 *
 * Responsável por finalizar NFC-e no modo PDV Desktop (Electron).
 *
 * Fluxo:
 *  1. Lê configurações do terminal local.
 *  2. Gera uuid_venda_terminal único.
 *  3. Usa proxima_venda / proximaNota como número da NFC-e.
 *  4. Monta dados_json no padrão do Form.vue (modelo, buyer, paymentModeCollection, etc.).
 *  5. Chama api.nfce.finalizar() — operação 100% atômica no SQLite:
 *       insere nfce_local + itens + pagamentos + fila_sync + incrementa proxima_venda.
 *  6. Tenta sincronização imediata com o backend, se online.
 *
 * Status fiscal inicial da NFC-e: 'pendente_xml'
 *   pendente_xml → xml_gerado → xml_assinado → transmitida → autorizada | rejeitada
 * Pré-venda permanece em 'pre_venda' e não possui XML até sua conversão.
 */
import { getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE } from "@/modules/Application";
import FiscalContextPdvDesktopService from "./FiscalContextPdvDesktopService";
import PdvDesktopConnectionService from "./PdvDesktopConnectionService";
import NfceOfflineFiscalService from "./NfceOfflineFiscalService";
import SyncPdvDesktopService from "./SyncPdvDesktopService";
import TerminalConfigService from "./TerminalConfigService";
import { normalizarPagamentoFiscal } from "./NfcePagamentoFiscalMapper";

function _getBaseUrl() {
  return (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
}

function _mensagemErroFiscal(err) {
  let msg = err && err.message ? err.message : String(err);
  msg = msg.replace(/^Error invoking remote method '[^']+':\s*/i, "");
  msg = msg.replace(/^Error:\s*/i, "");
  return msg;
}

/**
 * Monta o objeto de lote com a quantidade VENDIDA (não o saldo do estoque).
 * A quantidade é formatada no padrão pt-BR: "1,00".
 */
function _buildLoteBaixa(loteOrig, qtdVendida) {
  if (!loteOrig) return null;
  const qtdFormatada = Number(qtdVendida || 0).toFixed(2).replace('.', ',');
  return {
    id_produto_lote: loteOrig.id_produto_lote ?? null,
    id_empresa:      loteOrig.id_empresa      ?? null,
    id_produto:      loteOrig.id_produto      ?? null,
    quantidade:      qtdFormatada,
    numero_lote:     loteOrig.numero_lote     ?? null,
    data_fabricacao: loteOrig.data_fabricacao ?? null,
    data_validade:   loteOrig.data_validade   ?? null,
    updated_at:      loteOrig.updated_at_servidor ?? loteOrig.updated_at ?? null,
  };
}

/** Normaliza um item do carrinho, suportando tanto item direto quanto item.product */
function _normalizarItem(item) {
  const p    = item.product ?? item;
  const orig = p.produto_original ?? item.produto_original ?? {};
  const embalagem =
    item.embalagem ?? p.embalagem_selecionada ?? orig.embalagem_selecionada ?? null;
  const quantidade = Number(item.quantity ?? item.quantidade ?? p.quantidade ?? 1);
  const fatorConversao = Number(embalagem?.fator_conversao) || 1;
  return {
    id_produto:     p.id_produto      ?? null,
    uuid_produto:   p.uuid_produto    ?? p.uuid ?? orig.uuid ?? orig.uuid_sync ?? null,
    codigo:         p.codigo          != null ? String(p.codigo) : null,
    descricao:      p.descricao       ?? p.name ?? "",
    cod_barras:     embalagem?.cod_barras ?? p.cod_barras ?? null,
    quantidade,
    quantidade_baixa_estoque: quantidade * fatorConversao,
    valor_unitario: Number(item.price     ?? item.valor_unitario ?? p.valor_unitario ?? p.preco ?? 0),
    desconto:       Number(item.discount  ?? item.desconto       ?? p.desconto       ?? 0),
    acrescimo:      Number(item.acrescimo ?? p.acrescimo         ?? 0),
    total:          Number(item.totalPrice ?? item.total          ?? 0),
    // ── Ajustes por item (desconto/acréscimo detalhado) ───────────────────
    desconto_valor:        Number(p.desconto_valor       ?? item.desconto_valor       ?? 0),
    desconto_percentual:   Number(p.desconto_percentual  ?? item.desconto_percentual  ?? 0),
    acrescimo_valor:       Number(p.acrescimo_valor      ?? item.acrescimo_valor      ?? 0),
    acrescimo_percentual:  Number(p.acrescimo_percentual ?? item.acrescimo_percentual ?? 0),
    total_bruto:           Number(p.total_bruto   ?? item.total_bruto   ?? 0),
    total_liquido:         Number(p.total_liquido ?? item.total_liquido ?? item.total ?? 0),
    ajuste_tipo:           p.ajuste_tipo  ?? item.ajuste_tipo  ?? null,
    ajuste_forma:          p.ajuste_forma ?? item.ajuste_forma ?? null,
    // Campos fiscais planos — usados pelo repositório e pelo XML builder
    unidade_medida_sigla:          embalagem?.unidade_medida?.sigla ?? embalagem?.unidade_medida?.un ?? p.unidade_medida_sigla ?? orig.unidade_medida_sigla ?? null,
    ncm_codigo:                    p.ncm_codigo                    ?? orig.ncm_codigo                    ?? null,
    cfop_codigo:                   p.cfop_codigo                   ?? orig.cfop_codigo                   ?? null,
    origem_mercadoria:             p.origem_mercadoria             ?? p.id_origem                        ?? orig.origem_mercadoria             ?? orig.id_origem ?? orig.orig ?? "0",
    orig:                          p.orig                          ?? p.origem_mercadoria                ?? p.id_origem                        ?? orig.orig ?? orig.origem_mercadoria ?? orig.id_origem ?? "0",
    cst_csosn_codigo:              p.cst_csosn_codigo              ?? orig.cst_csosn_codigo              ?? null,
    cst_csosn_contribuinte_codigo: p.cst_csosn_contribuinte_codigo ?? orig.cst_csosn_contribuinte_codigo ?? null,
    cst_pis_codigo:                p.cst_pis_codigo                ?? orig.cst_pis_codigo                ?? null,
    pis:                           Number(p.pis    ?? orig.pis    ?? 0),
    cst_cofins_codigo:             p.cst_cofins_codigo             ?? orig.cst_cofins_codigo             ?? null,
    cofins:                        Number(p.cofins ?? orig.cofins ?? 0),
    // Campos de lote — lidos diretamente do item do carrinho
    id_produto_lote: p.id_produto_lote ?? null,
    numero_lote:     p.numero_lote     ?? null,
    data_validade:   p.data_validade   ?? null,
    data_fabricacao: p.data_fabricacao ?? null,
    // lote_original: objeto completo do lote, serializado no payload_json para fallback no XML builder
    lote_original:   p.lote_original   ?? null,
    // lote_baixa_json: lote com a quantidade VENDIDA (não o saldo) — para payload web-compatible
    lote_baixa_json: _buildLoteBaixa(
      p.lote_original ?? null,
      Number(item.quantity ?? item.quantidade ?? p.quantidade ?? 1)
    ),
    // ── Combinação ──────────────────────────────────────────────────────────
    id_produto_combinacao: item.id_produto_combinacao ?? null,
    descricao_combinacao:  item.descricao_combinacao  ?? null,
    // combinacao_json: objeto de atributos { "Tamanhos": "P", "quantidade": 1 }
    combinacao_json:       item.combinacao            ?? null,
    embalagem,
    // ── Produto completo para montagem do payload web-compatible ─────────────
    produto_original:    item.produto_original    ?? p.produto_original ?? null,
    tipo_preco_aplicado: item.tipo_preco_aplicado ?? null,
  };
}

// ── Helpers de rateio de desconto/acréscimo no nível da venda ────────────────
//
// Quando o operador aplica desconto/acréscimo no TOTAL da venda (e não por item),
// o valor precisa ser rateado proporcionalmente entre os itens para que o XML
// NFC-e fique coerente:
//   - <prod><vProd> permanece bruto e <vDesc> recebe o rateio do item
//   - <ICMSTot><vProd> = soma bruta, <vDesc> = soma rateios, <vNF> = líquido
// Isso evita a rejeição 610 ("Total da NF difere do somatório dos valores
// que compõem o valor total da NF").

const _round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

function _brutoItem(item) {
  const tb = Number(item.total_bruto) || 0;
  if (tb > 0) return _round2(tb);
  const q = Number(item.quantidade)     || 0;
  const u = Number(item.valor_unitario) || 0;
  return _round2(q * u);
}

function _recalcularTotalLiquidoItem(item) {
  const bruto = _brutoItem(item);
  const desc  = Number(item.desconto)  || 0;
  const acres = Number(item.acrescimo) || 0;
  const liquido = _round2(bruto - desc + acres);
  item.total_bruto    = bruto;
  item.desconto_valor = desc;
  item.acrescimo_valor = acres;
  item.total          = liquido;
  item.total_liquido  = liquido;
}

/**
 * Rateia um valor total ("desconto" ou "acréscimo") entre os itens,
 * proporcionalmente ao bruto de cada item. A diferença de centavos vai
 * para o último item, garantindo soma exata.
 *
 * @param {number} valorTotal       Valor a ratear (>= 0)
 * @param {Array<object>} itens     Itens já normalizados
 * @param {"desconto"|"acrescimo"} campo  Campo a acumular em cada item
 */
function _ratearValorTotalNosItens(valorTotal, itens, campo) {
  const valor = _round2(valorTotal);
  if (valor <= 0 || !itens || itens.length === 0) return;

  const brutos = itens.map(_brutoItem);
  const somaBruto = _round2(brutos.reduce((s, b) => s + b, 0));
  if (somaBruto <= 0) return;

  let acumulado = 0;
  itens.forEach((item, idx) => {
    let parcela;
    if (idx === itens.length - 1) {
      parcela = _round2(valor - acumulado);
    } else {
      parcela = _round2((valor * brutos[idx]) / somaBruto);
      acumulado = _round2(acumulado + parcela);
    }
    if (parcela < 0) parcela = 0;
    item[campo] = _round2((Number(item[campo]) || 0) + parcela);
  });
}

function ratearDescontoTotalNosItens(descontoTotal, itens) {
  _ratearValorTotalNosItens(descontoTotal, itens, "desconto");
}

function ratearAcrescimoTotalNosItens(acrescimoTotal, itens) {
  _ratearValorTotalNosItens(acrescimoTotal, itens, "acrescimo");
}

const NfcePdvDesktopService = {
  /** Cria a pré-venda sem gerar XML ou exigir dados fiscais dos produtos. */
  finalizarPreVenda(dados) {
    return this.finalizarNfce(dados, { preVenda: true });
  },

  /**
   * Finaliza uma NFC-e no contexto Desktop/Electron.
   *
   * Fluxo:
   *   1. Valida contexto fiscal.
   *   2. Lê configurações do terminal.
   *   3. Decide tpEmis (1=online, 9=offline).
   *   4. Persiste nfce_local + itens + pagamentos atomicamente.
   *   5. Gera e assina XML.
   *   6. Se online (tpEmis=1): tenta transmitir imediatamente ao backend.
   *   7. Retorna resultado estruturado: { success, autorizada, contingencia, rejeitada, ... }
   *
   * @param {object} dados  - payload da venda
   * @returns {Promise<{
   *   success:             boolean,
   *   autorizada:          boolean,
   *   contingencia:        boolean,
   *   rejeitada:           boolean,
   *   imprimir:            boolean,
   *   uuid_venda_terminal: string,
   *   message:             string,
   *   protocolo?:          string,
   *   codigo_rejeicao?:    string,
   * }>}
   */
  async finalizarNfce(dados, options = {}) {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");
    const preVenda = options.preVenda === true;

    // Toda operação exige um terminal autorizado. As validações de emissão
    // fiscal, porém, são exclusivas da NFC-e: pré-venda é um documento não fiscal.
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    let validacao = { emitente: null, configuracao: null };

    if (!preVenda) {
      await TerminalConfigService.garantirConfiguracaoNfceLocal();
      await TerminalConfigService.garantirConfiguracaoTerminalParaEmissao();

      // ── 0. Valida contexto fiscal somente para emissão NFC-e ────────────────
      validacao = await FiscalContextPdvDesktopService.validarContextoFiscalLocal();
      if (!validacao.valido) {
        const detalhe = validacao.problemas.join(" | ");
        const err = new Error(validacao.mensagem ||
          `Contexto fiscal incompleto. Sincronize os dados fiscais antes de emitir NFC-e. Problemas: ${detalhe}`
        );
        err.code = validacao.codigo || "CONTEXTO_FISCAL_INCOMPLETO";
        throw err;
      }

      if (api.ibpt?.ensure) {
        console.log("[IBPT] Preparando tabela IBPT para emissão de NFC-e...");
        try {
          const ibptResult = await api.ibpt.ensure(validacao.emitente?.uf);
          if (ibptResult && ibptResult.ok === false) {
            console.warn("[IBPT] Tabela indisponível; emissão seguirá sem informação IBPT.", ibptResult.message);
          }
        } catch (err) {
          console.warn("[IBPT] Falha ao preparar tabela; emissão seguirá sem informação IBPT.", err?.message || err);
        }
      }
    }

    // ── 1. Lê configurações do terminal ──────────────────────────────────────
    const [
      id_terminal_venda_raw,
      uuid_terminal,
      id_empresa_raw,
      id_configuracao,
      proxima_venda_str,
    ] = await Promise.all([
      api.config.get("id_terminal_venda"),
      api.config.get("uuid_terminal"),
      api.config.get("id_empresa"),
      api.config.get("id_configuracao"),
      api.config.get("proxima_venda"),
    ]);

    const id_terminal_venda = id_terminal_venda_raw ? parseInt(id_terminal_venda_raw, 10) : null;
    const id_empresa        = id_empresa_raw        ? parseInt(id_empresa_raw, 10)        : null;
    const proximaVenda      = parseInt(proxima_venda_str || "1", 10);
    const serie             = validacao.configuracao?.serie
      ? parseInt(validacao.configuracao.serie, 10)
      : (dados.serie ?? null);
    const agora             = new Date().toISOString();
    const buyer             = dados.buyer || {};

    // ── 1.5. Decide modo de emissão com base na conectividade ─────────────────
    const backendOnline = await PdvDesktopConnectionService.verificarConexaoBackend();
    console.log('[PDV DESKTOP] backendOnline:', backendOnline);
    // Pré-venda não é emissão fiscal; tpEmis é apenas um valor neutro para o
    // registro local e nenhum XML será criado nesta etapa.
    const tpEmis = preVenda ? 1 : (backendOnline ? 1 : 9);
    console.log('[PDV DESKTOP] tpEmis decidido:', tpEmis);

    // ── 2. Gera identificador único ──────────────────────────────────────────
    const uuid_venda_terminal = crypto.randomUUID();

    // ── 2.5. Reserva número fiscal somente para NFC-e ───────────────────────
    // Pré-venda usa exclusivamente a numeração comercial. O número NFC-e será
    // reservado apenas quando o operador efetivamente convertê-la em NFC-e.
    let reserva = null;
    let proximaNota = proximaVenda;
    if (!preVenda) {
      reserva = await api.nfce.reservarNumero({
        uuid_terminal,
        id_empresa,
        serie,
        modelo: 65,
      });
      proximaNota = reserva.numeroReservado;

      console.log('[NFCE_NUMERACAO_RESERVA]', {
        proximaNotaAntes:  proximaNota,
        numeroReservado:   proximaNota,
        proximaNotaDepois: reserva.proximaNotaDepois,
        serie:             reserva.serie,
        uuid_venda_terminal,
      });
    }

    // ── 3. Normaliza itens e pagamentos ──────────────────────────────────────
    const itens = (dados.productOfTradeCollection || []).map(_normalizarItem);

    // ── 3.1 Rateio do desconto/acréscimo aplicados no TOTAL da venda ────────
    // Se o operador aplicou desconto/acréscimo no total (e não por item), o
    // valor é distribuído proporcionalmente ao bruto de cada item, acumulando
    // sobre o desconto/acréscimo já existente por item. Em seguida o total
    // líquido de cada item é recalculado para refletir o ajuste.
    const descontoVenda  = Number(dados.desconto  ?? dados.discount   ?? 0) || 0;
    const acrescimoVenda = Number(dados.acrescimo ?? dados.extraValue ?? 0) || 0;
    if (descontoVenda > 0)  ratearDescontoTotalNosItens(descontoVenda, itens);
    if (acrescimoVenda > 0) ratearAcrescimoTotalNosItens(acrescimoVenda, itens);
    itens.forEach(_recalcularTotalLiquidoItem);

    // O valor total da nota deve casar exatamente com a soma dos líquidos
    // dos itens (vNF = Σ vProd - Σ vDesc + Σ vOutro), para evitar rejeição 610.
    const valorTotalRateado = _round2(
      itens.reduce((s, i) => s + (Number(i.total) || 0), 0),
    );

    itens.forEach((item) => console.log('[NFCE_ITEM] salvando item:', item));

    const observacaoVenda = String(
      dados.observation ?? dados.observacao ?? ""
    ).trim();

    const pagamentos = (dados.paymentModeCollection || []).map((pag) => {
      const pagamentoBase = {
        ...pag,
        id_forma_pagamento: pag.id_forma_pagamento ?? pag.id ?? null,
        id_meio_pagamento:  pag.id_meio_pagamento  ?? null,
        descricao:          pag.descricao          ?? null,
        valor:              Number(pag.value ?? pag.valor ?? 0),
        parcelas:           pag.vezes              ?? pag.parcelas ?? 1,
        vencimento:         (pag.range && pag.range[0]) ? pag.range[0].date : (pag.vencimento ?? null),
        tpag:               pag.tpag ?? pag.tPag ?? pag.codigo_pagamento_nfce ?? null,
        xPag:               pag.xPag ?? pag.xpag ?? null,
      };
      return normalizarPagamentoFiscal(pagamentoBase);
    });
    const dadosComPagamentosNormalizados = {
      ...dados,
      paymentModeCollection: pagamentos,
    };

    // ── 4. Monta dados_json no padrão Form.vue ───────────────────────────────
    const dados_json = JSON.stringify({
      modelo:                    65,
      tpNF:                      dados.tpNF          ?? 1,
      serie,
      price:                     dados.price         ?? 0,
      discount:                  dados.desconto      ?? dados.discount     ?? 0,
      extraValue:                dados.acrescimo     ?? dados.extraValue   ?? 0,
      freightPrice:              dados.freightPrice  ?? 0,
      observation:                observacaoVenda,
      observacao:                 observacaoVenda,
      buyer,
      seller:                    dados.seller        ?? null,
      id_vendedor:               dados.id_vendedor   ?? null,
      paymentModeCollection:     pagamentos,
      productOfTradeCollection:  dados.productOfTradeCollection || [],
      status:                    preVenda ? 7 : 1,
      statusTransmissao:         preVenda ? null : "OFFLINE",
      isNFce:                    !preVenda,
      pre_venda:                 preVenda,
      isPDVSync:                 true,
      sync_app:                  true,
      origem_venda:              "pdv_offline",
      uuid_terminal,
      id_configuracao,
      id_empresa,
      numero:                    proximaNota,
      numeroNota:                preVenda ? null : proximaNota,
      numero_venda:              proximaVenda,
      uuid_venda_terminal,
    });

    // ── 5. Monta payload para fila_sync ──────────────────────────────────────
    const payload_json = JSON.stringify({
      uuid_venda_terminal,
      uuid_terminal,
      id_terminal_venda,
      id_empresa,
      id_configuracao,
      modelo:               65,
      serie,
      numero:               proximaNota,
      numeroNota:           preVenda ? null : proximaNota,
      numero_venda:         proximaVenda,
      status:               preVenda ? 7 : 1,
      statusTransmissao:    preVenda ? null : "OFFLINE",
      valorTotal:           valorTotalRateado,
      dtVenda:              agora,
      emissao:              agora,
      identificacaoCliente: buyer.cnpj_cpf ?? buyer.cpf ?? null,
      nomeCliente:          buyer.nome_fantasia ?? buyer.nome ?? null,
      origem_venda:         "pdv_offline",
      pre_venda:            preVenda,
      isPDVSync:            true,
      sync_app:             true,
      observation:          observacaoVenda,
      observacao:           observacaoVenda,
      dados:                dadosComPagamentosNormalizados,
    });

    // ── 6. Persiste tudo atomicamente (SQLite transaction) ───────────────────
    await api.nfce.finalizar({
      // Cabeçalho nfce_local
      uuid_venda_terminal,
      id_terminal_venda,
      id_empresa,
      id_configuracao:      id_configuracao  ?? null,
      modelo:               65,
      serie,
      numero:               proximaNota,
      numeroNota:           preVenda ? null : proximaNota,
      numero_venda:         proximaVenda,
      chave:                null,
      cNF:                  null,
      identificacaoCliente: buyer.cnpj_cpf   ?? buyer.cpf ?? null,
      nomeCliente:          buyer.nome_fantasia ?? buyer.nome ?? null,
      valorTotal:           valorTotalRateado,
      dtVenda:              agora,
      emissao:              agora,
      status:               preVenda ? 7 : 1,
      statusTransmissao:    preVenda ? null : "OFFLINE",
      origem_venda:         "pdv_desktop",
      status_local:         preVenda ? "pre_venda" : "finalizada",
      status_fiscal:        preVenda ? "pre_venda" : "pendente_xml",
      status_sync:          "pendente",
      tipo_emissao:         tpEmis,
      id_vendedor:          dados.id_vendedor ?? null,
      dados_json,
      payload_json,
      // Controle de numeração para incremento atômico
      proximaVenda,
      // Itens e pagamentos (tratados pelo makeNfceRepo.finalizar)
      itens,
      pagamentos,
    });

    console.log('[NFCE_NUMERACAO_VENDA]', {
      uuid_venda_terminal,
      numero: proximaNota,
      serie,
      chave: null, // preenchida após geração do XML
    });

    // Pré-venda não possui reserva fiscal. O vínculo existe somente para NFC-e.
    if (!preVenda) {
      try {
        await api.nfce.vincularVenda(proximaNota, uuid_venda_terminal, null);
      } catch (_) { /* não crítico — rastreabilidade apenas */ }
    }

    // A pré-venda é não fiscal: não gera XML, não reserva número de NFC-e e
    // não exige NCM, CFOP, CSOSN/CST ou certificado. Ela é sincronizada como
    // venda comum; a validação fiscal ocorrerá somente durante a conversão.
    if (preVenda) {
      let synced = false;
      let syncError = null;

      if (backendOnline) {
        try {
          const resultado = await SyncPdvDesktopService.sincronizarPreVenda(uuid_venda_terminal);
          synced = resultado?.status === "sincronizada" || resultado?.status === "ja_sincronizada";
          if (!synced) {
            syncError = resultado?.erro ?? resultado?.message ?? "O backend não confirmou a pré-venda.";
          }
        } catch (errSync) {
          syncError = errSync?.message ?? String(errSync);
          console.warn("[NfcePdvDesktopService] Pré-venda ficará pendente de sincronização:", syncError);
        }
      }

      await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "pre_venda", {
        status_sync: synced ? "sincronizada" : "pendente",
        ultimo_erro: syncError,
      });
      return {
        success: true,
        pre_venda: true,
        xml_gerado: false,
        synced,
        autorizada: false,
        contingencia: false,
        rejeitada: false,
        imprimir: false,
        uuid_venda_terminal,
        numero_venda: proximaVenda,
        numero_nfce: null,
        message: "Pré-Venda gerada com sucesso!",
      };
    }

    // ── 6.5 Gera e assina XML imediatamente somente para NFC-e ───────────────
    try {
      await NfceOfflineFiscalService.gerarEAssinarXml(uuid_venda_terminal, { tpEmis });
    } catch (errXml) {
      const msg = _mensagemErroFiscal(errXml);
      console.error("[NfcePdvDesktopService] Falha ao gerar/assinar XML:", msg);
      // A numeração já foi reservada e incrementada — não reutilizar este número.
      // A venda fica com status_fiscal = 'erro' para correção manual em Vendas Offline.
      try {
        await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "erro", {
          status_sync: "erro",
          motivo_erro: msg,
          ultimo_erro: msg,
        });
        await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "erro_transmissao");
      } catch (_) { /* ignora falha de update */ }
      throw new Error(`Falha ao gerar/assinar XML: ${msg}`);
    }

    // ── 7. Transmite imediatamente se online (tpEmis=1) ──────────────────────
    if (tpEmis === 1) {
      try {
        const resultado = await SyncPdvDesktopService.transmitirNfce(uuid_venda_terminal);
        const status    = resultado?.status;
        console.log("[NfcePdvDesktopService] Resultado transmissão:", status, resultado?.protocolo);

        if (status === "autorizada" || status === "ja_autorizada") {
          try { await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "autorizado"); } catch (_) { /* ignora falha ao atualizar status */ }
          return {
            success:             true,
            autorizada:          true,
            contingencia:        false,
            rejeitada:           false,
            imprimir:            true,
            uuid_venda_terminal,
            protocolo:           resultado.protocolo  ?? null,
            message:             "NFC-e autorizada com sucesso.",
          };
        }

        if (status === "rejeitada") {
          // Número rejeitado não é reutilizado — fica registrado como rejeitado
          try { await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "rejeitado"); } catch (_) { /* ignora falha ao atualizar status */ }
          return {
            success:             false,
            autorizada:          false,
            contingencia:        false,
            rejeitada:           true,
            imprimir:            false,
            uuid_venda_terminal,
            codigo_rejeicao:     resultado.codigo ?? null,
            message:             resultado.motivo ?? "NFC-e rejeitada pela SEFAZ.",
          };
        }

        // Status inesperado do backend — trata como erro de transmissão
        const msgStatus = `Status inesperado: ${status}`;
        console.warn("[NfcePdvDesktopService] Status de transmissão não reconhecido:", status);
        await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "erro_transmissao", {
          status_sync: "erro",
          ultimo_erro: msgStatus,
        });
        try { await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "erro_transmissao"); } catch (_) { /* ignora falha ao atualizar status */ }
        return {
          success:             false,
          autorizada:          false,
          contingencia:        false,
          rejeitada:           false,
          erro_transmissao:    true,
          imprimir:            false,
          uuid_venda_terminal,
          message:             `NFC-e emitida mas não transmitida (${msgStatus}). Verifique em Vendas Offline.`,
        };
      } catch (errTransmissao) {
        // Falha de rede/timeout com XML já gerado com tpEmis=1
        // Não pode ser tratada como contingência — o XML NÃO tem dhCont/xJust
        const msg = errTransmissao.message || "Falha de comunicação com o backend";
        console.warn("[NfcePdvDesktopService] Falha ao transmitir (tpEmis=1):", msg);
        try {
          await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "erro_transmissao", {
            status_sync: "erro",
            ultimo_erro: msg,
          });
          await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "erro_transmissao");
        } catch (_) { /* não crítico */ }
        return {
          success:             false,
          autorizada:          false,
          contingencia:        false,
          rejeitada:           false,
          erro_transmissao:    true,
          imprimir:            false,
          uuid_venda_terminal,
          message:             `NFC-e gerada mas não transmitida. Tente novamente em Vendas Offline.`,
        };
      }
    }

    // tpEmis=9 → contingência offline
    return {
      success:        true,
      autorizada:     false,
      contingencia:   true,
      rejeitada:      false,
      imprimir:       true,
      uuid_venda_terminal,
      message:        "NFC-e emitida em contingência. Será transmitida quando houver conexão.",
    };
  },

  /**
   * Converte explicitamente uma pré-venda local em NFC-e.
   * Reserva a numeração fiscal, regenera o XML como NFC-e e só então transmite.
   */
  async converterPreVendaEmNfce(uuid_venda_terminal) {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();
    await TerminalConfigService.garantirConfiguracaoTerminalParaEmissao();

    const nfce = await api.nfce.getByUuid(uuid_venda_terminal);
    if (!nfce) throw new Error("Pré-venda não encontrada no banco local.");
    if (nfce.status_fiscal === "autorizada") {
      return {
        status: "ja_autorizada",
        protocolo: nfce.protocolo ?? null,
        uuid_venda_terminal,
      };
    }
    if (nfce.status_fiscal !== "pre_venda") {
      throw new Error("Somente registros com status de pré-venda podem ser convertidos em NFC-e.");
    }
    // Garante que financeiro/estoque sejam criados antes da conversão. O UUID
    // é o mesmo nas duas operações, permitindo ao backend tratar o upgrade de
    // pré-venda para NFC-e sem duplicar a venda.
    if (nfce.status_sync !== "sincronizada") {
      const sync = await SyncPdvDesktopService.sincronizarPreVenda(uuid_venda_terminal);
      if (sync?.status !== "sincronizada" && sync?.status !== "ja_sincronizada") {
        throw new Error(sync?.erro ?? sync?.message ?? "A pré-venda ainda não foi sincronizada.");
      }
    }

    const [uuid_terminal, id_empresa_raw] = await Promise.all([
      api.config.get("uuid_terminal"),
      api.config.get("id_empresa"),
    ]);
    const id_empresa = id_empresa_raw ? parseInt(id_empresa_raw, 10) : null;

    // Pré-vendas antigas já reservaram indevidamente um número NFC-e. Nesse
    // caso, reutilizamos a reserva existente para não abrir um segundo buraco.
    const possuiReservaLegada = nfce.numeroNota != null &&
      String(nfce.numeroNota) !== "" &&
      String(nfce.numeroNota) !== String(nfce.numero_venda ?? "");
    let numeroFiscal = possuiReservaLegada ? Number(nfce.numeroNota) : null;
    let serieFiscal = Number(nfce.serie);

    if (!possuiReservaLegada) {
      const reserva = await api.nfce.reservarNumero({
        uuid_terminal,
        id_empresa,
        serie: nfce.serie,
        modelo: 65,
      });
      numeroFiscal = reserva.numeroReservado;
      serieFiscal = reserva.serie;
    }

    await api.nfce.prepararConversaoPreVenda(uuid_venda_terminal, {
      numero: numeroFiscal,
      serie: serieFiscal,
    });
    try {
      await api.nfce.vincularVenda(numeroFiscal, uuid_venda_terminal, null);
    } catch (_) { /* rastreabilidade não bloqueia a conversão */ }

    try {
      const xmlGerado = await NfceOfflineFiscalService.gerarEAssinarXml(
        uuid_venda_terminal,
        { tpEmis: 1 }
      );
      try {
        await api.nfce.vincularVenda(
          numeroFiscal,
          uuid_venda_terminal,
          xmlGerado?.chave ?? null
        );
      } catch (_) { /* não crítico */ }
    } catch (error) {
      try {
        await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "erro_transmissao");
      } catch (_) { /* não crítico */ }
      throw new Error(`Falha ao gerar o XML da NFC-e: ${error?.message ?? error}`);
    }

    const resultado = await SyncPdvDesktopService.transmitirNfce(uuid_venda_terminal);
    if (resultado?.status === "autorizada" || resultado?.status === "ja_autorizada") {
      try {
        await api.nfce.atualizarStatusNumeracao(uuid_venda_terminal, "autorizado");
      } catch (_) { /* não crítico */ }
    }
    return { ...resultado, uuid_venda_terminal };
  },
};

export default NfcePdvDesktopService;
